import "./ProyectosCommon.css";
import { SnapshotResponse } from "../../../shared/response/proyectos/SnapshotResponse";
import { SnapshotService } from "../../services/proyectos/SnapshotService";
import { SnapshotFormModal } from "./SnapshotFormModal";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { fmtCentavos, fmtPct } from "../../../shared/utils/proyectosFormat";

/** Panel de SMMLV + IPC por año, montado dentro de ParametrosPage. Antes era SnapshotsPage completa. */
export class MacroeconomicoPanel {
  private _all: SnapshotResponse[] = [];
  private _tbody!: HTMLTableSectionElement;

  public render(container: HTMLElement): void {
    container.innerHTML = `
      <div class="pr-pg-hd">
        <div>
          <div class="pr-pg-ti">Parámetros macroeconómicos</div>
          <div class="pr-pg-su">SMMLV + IPC por año. Un snapshot activo congela los parámetros de los proyectos nuevos.</div>
        </div>
        <button class="sx-btn sx-btn-primary sx-btn-sm" id="snp-nuevo"><i class="ph ph-plus"></i> Nuevo snapshot</button>
      </div>

      <div class="pr-tbl-wrap">
        <table class="pr-tbl">
          <thead>
            <tr>
              <th>Año</th><th>SMMLV</th><th>Factor jornal</th><th>IPC</th><th>IPC real</th>
              <th>Estado</th><th>Nota</th><th>Acciones</th>
            </tr>
          </thead>
          <tbody id="snp-tbody"><tr><td colspan="8" class="pr-empty">Cargando…</td></tr></tbody>
        </table>
      </div>
    `;

    this._tbody = container.querySelector("#snp-tbody")! as HTMLTableSectionElement;
    container.querySelector<HTMLButtonElement>("#snp-nuevo")!.onclick = () => this._onCreate();
    void this._loadData();
  }

  private async _loadData(): Promise<void> {
    try {
      const items = await SnapshotService.instance.get();
      this._all = items.filter((s) => !s.isEliminado).sort((a, b) => b.año - a.año);
      this._renderRows();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar snapshots", (error as Error).message);
      this._tbody.innerHTML = `<tr><td colspan="8" class="pr-empty">Error al cargar</td></tr>`;
    }
  }

  private _renderRows(): void {
    if (this._all.length === 0) {
      this._tbody.innerHTML = `<tr><td colspan="8" class="pr-empty">Sin snapshots. Crea el primero.</td></tr>`;
      return;
    }
    this._tbody.innerHTML = this._all.map((s, i) => `
      <tr>
        <td class="pr-num" style="font-weight:var(--sx-weight-semibold)">${s.año}</td>
        <td class="pr-num">${fmtCentavos(s.smmlv)}</td>
        <td class="pr-num">${fmtCentavos(s.factorJornal)}</td>
        <td class="pr-num">${fmtPct(s.ipc)}</td>
        <td class="pr-num">${s.ipcReal != null ? fmtPct(s.ipcReal) : "<span style='color:var(--sx-text-tertiary)'>—</span>"}</td>
        <td>${s.activo ? `<span class="pr-pill ok"><span class="pr-dot"></span>Activo</span>` : `<span class="pr-pill na">Inactivo</span>`}</td>
        <td style="color:var(--sx-text-tertiary)">${this._esc(s.nota ?? "—")}</td>
        <td>
          <div class="pr-actions">
            ${s.activo ? "" : `<button class="sx-btn sx-btn-secondary sx-btn-sm" data-act="activar" data-idx="${i}">Activar</button>`}
            <button class="sx-btn sx-btn-ghost sx-btn-sm" data-act="ipc" data-idx="${i}">IPC real</button>
            <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-act="editar" data-idx="${i}" title="Editar"><i class="ph ph-pencil"></i></button>
          </div>
        </td>
      </tr>`).join("");

    this._tbody.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((btn) => {
      const s = this._all[Number(btn.dataset.idx)];
      btn.onclick = () => {
        if (btn.dataset.act === "activar") void this._onActivar(s);
        if (btn.dataset.act === "editar")  this._onEdit(s);
        if (btn.dataset.act === "ipc")     void this._onIpcReal(s);
      };
    });
  }

  private _onCreate(): void {
    new SnapshotFormModal((saved) => {
      this._all = [saved, ...this._all].sort((a, b) => b.año - a.año);
      this._renderRows();
    }).init();
  }

  private _onEdit(s: SnapshotResponse): void {
    new SnapshotFormModal((saved) => {
      this._all = this._all.map((x) => (x.id === saved.id ? saved : x));
      this._renderRows();
    }, s).init();
  }

  private async _onActivar(s: SnapshotResponse): Promise<void> {
    try {
      await SnapshotService.instance.activar(s.id);
      // Refrescar todo: activar desactiva los demás.
      this._all = (await SnapshotService.instance.get())
        .filter((x) => !x.isEliminado).sort((a, b) => b.año - a.año);
      this._renderRows();
      errorMessageComponent.addSuccess("Snapshot activado", `Parámetros ${s.año} vigentes para proyectos nuevos.`);
    } catch (error) {
      errorMessageComponent.addError("Error al activar", (error as Error).message);
    }
  }

  private async _onIpcReal(s: SnapshotResponse): Promise<void> {
    const raw = window.prompt(`IPC real DANE ${s.año} (%)`, s.ipcReal != null ? String(Number(s.ipcReal) * 100) : "");
    if (raw == null) return;
    const pct = Number(raw.replace(",", "."));
    if (isNaN(pct) || pct < 0) { errorMessageComponent.addWarning("Valor inválido", "Ingresa un porcentaje válido."); return; }
    try {
      const saved = await SnapshotService.instance.setIpcReal(s.id, { ipcReal: pct / 100 });
      this._all = this._all.map((x) => (x.id === saved.id ? saved : x));
      this._renderRows();
    } catch (error) {
      errorMessageComponent.addError("Error al registrar IPC real", (error as Error).message);
    }
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
